import { useCallback, useMemo } from "react"
import { mutate } from "swr"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { PaginatedResponse } from "models/PaginatedResponse"
import { omit } from "lib/helpers"
import { sendApiData } from "lib/api"
import { useApiData } from "./useApiData"

export interface FlashMessage {
  id: number
  title: string
  message: string
  createdAt: string
  displayType: `modal` | `note`
}

export interface FlashMessageWithDismissedState extends FlashMessage {
  dismissed: boolean
}

/**
 * @typedef DismissedMessage
 * @property {number} id ID of the message that was dismissed
 * @property {number} dismissedAt Timestamp of when the message was dismissed
 * @property {boolean} soft Message dissmisal state was persisted in browser rather than the DB
 */
interface DismissedMessage {
  id: number
  dismissedAt: number
}

interface DismissedMessagesStore {
  dismissedMessages: DismissedMessage[]
  addDismissedMessage: (id: number) => void
  removeDismissedMessage: (id: number) => void
}

const useDismissedMessagesStore = create<DismissedMessagesStore>()(
  persist(
    (set, get) => ({
      dismissedMessages: [],
      addDismissedMessage: id => {
        set({
          dismissedMessages: get()
            .dismissedMessages.filter(
              message => Date.now() - message.dismissedAt <= 3600000
            )
            .concat({
              id,
              dismissedAt: Date.now()
            })
        })
      },
      removeDismissedMessage: id => {
        set({
          dismissedMessages: get().dismissedMessages.filter(
            message =>
              Date.now() - message.dismissedAt <= 3600000 && message.id !== id
          )
        })
      }
    }),
    {
      name: `dismissed-flash-messages`
    }
  )
)

function useFlashMessages(page = 1) {
  const { dismissedMessages, addDismissedMessage, removeDismissedMessage } =
    useDismissedMessagesStore()
  const response = useApiData<PaginatedResponse<FlashMessage>>(
    `brands/notifications/flash-messages/?page=${page}`,
    {
      dedupingInterval: 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const filteredResults = useMemo<FlashMessageWithDismissedState[]>(
    () =>
      response.data?.results.map(message => ({
        ...message,
        dismissed: Boolean(
          dismissedMessages.find(dismissed => dismissed.id === message.id)
        )
      })) ?? [],
    [dismissedMessages, response.data?.results]
  )

  const dismissFlashMessage = useCallback(
    async ({ id, soft = false }: { id: number; soft?: boolean }) => {
      try {
        addDismissedMessage(id)
        if (!soft) {
          await sendApiData(
            `brands/notifications/flash-messages/${id}/mark-as-read`,
            {
              method: `POST`,
              retry: {
                limit: 3
              }
            }
          )
        }
      } catch (error) {
        removeDismissedMessage(id)
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        mutate(/brands\/notifications\/flash-messages\//)
      }
    },
    [addDismissedMessage, removeDismissedMessage]
  )

  const resetOldDismissedMessages = useCallback(() => {
    const now = Date.now()

    const timeoutAge = 86400000 // 24 hours

    dismissedMessages.forEach(message => {
      if (now - message.dismissedAt > timeoutAge) {
        removeDismissedMessage(message.id)
      }
    })
  }, [dismissedMessages, removeDismissedMessage])

  return useMemo(
    () => ({
      ...omit(response, `data`),
      data: response.data
        ? { ...response.data, results: filteredResults }
        : undefined,
      dismissFlashMessage,
      resetOldDismissedMessages
    }),
    [dismissFlashMessage, filteredResults, response, resetOldDismissedMessages]
  )
}

export default useFlashMessages
